import {
  MenuContainer,
  MenuGroup,
  useBetaStatus,
  View,
  zero,
} from "@streamplace/components";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView } from "react-native";
import { useStore } from "store";
import { useIsReady, useServerSettings, useStreamplaceUrl } from "store/hooks";
import { SettingToggle } from "./components/setting-toggle";

export function PrivacyCategorySettings() {
  const { t } = useTranslation("settings");
  const isReady = useIsReady();
  const serverSettings = useServerSettings();
  const url = useStreamplaceUrl();
  const getServerSettingsFromPDS = useStore(
    (state) => state.getServerSettingsFromPDS,
  );
  const createServerSettingsRecord = useStore(
    (state) => state.createServerSettingsRecord,
  );
  const savingRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const debugRecordingOn = serverSettings?.debugRecording === true;
  // Defaults on (unlike debugRecording): only an explicit `false` turns it off.
  const livestreamRecordingOn = serverSettings?.livestreamRecording !== false;
  // Opt-in: only an explicit `true` turns it on.
  const autoPublishVodsOn = serverSettings?.autoPublishVods === true;
  // The livestream-recording and automatic VOD publishing toggles are only
  // meaningful for accounts in the VOD beta — the node won't record anyone
  // else regardless of these flags — so we only surface them to them.
  const { status: vodBetaStatus } = useBetaStatus("vod");

  useEffect(() => {
    if (isReady) {
      getServerSettingsFromPDS();
    }
  }, [isReady]);

  const saveSetting = async (
    patch: Parameters<typeof createServerSettingsRecord>[0],
  ) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      await createServerSettingsRecord(patch);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const u = new URL(url);

  return (
    <ScrollView>
      <View style={[zero.layout.flex.align.center, zero.px[2], zero.py[2]]}>
        <View style={{ maxWidth: 500, width: "100%" }}>
          <MenuContainer>
            <MenuGroup>
              <SettingToggle
                title={t("debug-recording-title", { host: u.host })}
                description={t("debug-recording-description")}
                value={debugRecordingOn}
                disabled={saving}
                onValueChange={(value) =>
                  saveSetting({ debugRecording: value })
                }
              />
              {vodBetaStatus === "granted" && (
                <>
                  <SettingToggle
                    title={t("livestream-recording-title", { host: u.host })}
                    description={t("livestream-recording-description")}
                    value={livestreamRecordingOn}
                    disabled={saving}
                    onValueChange={(value) =>
                      saveSetting({ livestreamRecording: value })
                    }
                    testID="settings-livestream-recording"
                  />
                  <SettingToggle
                    title={t("auto-publish-vods-title")}
                    description={t("auto-publish-vods-description", {
                      host: u.host,
                    })}
                    value={autoPublishVodsOn}
                    disabled={saving}
                    onValueChange={(value) =>
                      saveSetting({ autoPublishVods: value })
                    }
                    testID="settings-auto-publish-vods"
                  />
                </>
              )}
            </MenuGroup>
          </MenuContainer>
        </View>
      </View>
    </ScrollView>
  );
}
